import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { groupVariationsByFirstLevelButton } from '@cart-services/utils/groupVariationsByFirstLevelButton'
import { Amount } from '@cart-ui/Amount/Amount'
import { CouponCondition } from '@cart-ui/CouponCondition/CouponCondition'
import { projectNameMap } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { AppliedStatus } from '@hooks-api/types/dataModel/coupon'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Chip } from '@pets-ui/Chip/Chip'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { CartFilled } from '@pets-ui/Icons'
import { Image } from '@pets-ui/Image/Image'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { type ItemWantToAdd, useHandleProductAddToCart } from '../../fp-b2b-shared/hooks/use-handle-product-add-to-cart'
import { useProductCatalog } from '../../fp-b2b-shared/hooks/use-product-catalog'
import { DialogWeightAttribute } from '../../merchant/product-catalog/components/DialogWeightAttribute/DialogWeightAttribute'
import { brandImageMap } from '../constants'
import { DialogDeliveryClass } from '../product-catalog/components/DialogDeliveryClass'
import { ProductVariationRow } from './ProductVariationRow'

export const DIALOG_PRODUCT_CART = 'DIALOG_PRODUCT_CART'

export interface DialogProductCartProps {
  title: string
  thumbnail: string
  variations: Variation[]
  basePath: string
}

interface DialogProductCartContentProps extends DialogProductCartProps {
  onClose: () => void
}

const DialogProductCartContent = (props: DialogProductCartContentProps) => {
  const { title, thumbnail, variations, basePath, onClose } = props

  const { campaignId, projectCode, countryCode, campaignCode, currency, platform, cartTotalQuantity, cartTotalPrice } = useProductCatalog()
  const { getMergedCouponCriterions } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const { items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { handleProductAddToCart } = useHandleProductAddToCart()
  const history = useHistory()

  const avatarSrc = brandImageMap[projectCode]?.avatar
  const brandName = projectNameMap[projectCode]

  const variationsGroup = useMemo(
    () => groupVariationsByFirstLevelButton(variations),
    [variations],
  )

  const firstLevelButtonKeys = useMemo(() => Object.keys(variationsGroup), [variationsGroup])
  const [currentGroup, setCurrentGroup] = useState<string | undefined>(firstLevelButtonKeys[0])

  const currentVariations = useMemo(() =>
    currentGroup ? variationsGroup[currentGroup] ?? [] : [],
  [currentGroup, variationsGroup])

  const couponIds = useMemo(() =>
    [...new Set(currentVariations.flatMap(variation => variation.coupon_ids ?? []))],
  [currentVariations])

  const couponConditions = useMemo(() => getMergedCouponCriterions(couponIds), [couponIds, getMergedCouponCriterions])

  const handleQuantityChange = (variationItem: Variation) => (quantity?: number) => {
    const itemWantToAdd: ItemWantToAdd = {
      ...variationItem,
      delivery_class: variationItem.delivery_class,
      cartItemId: variationItem.variation_id,
      quantity,
    }
    handleProductAddToCart(itemWantToAdd)
  }

  const handleNavigateToCart = () => {
    onClose()
    history.push(`${basePath}/cart`)
  }

  return (
    <>
      <DialogHeader onClose={onClose}>
        <HStack gap={2} alignItems="center">
          {avatarSrc && <Image src={avatarSrc} alt={brandName} width={24} height={24} borderRadius={24} />}
          <Text color={colors.grey600}>{brandName}</Text>
        </HStack>
      </DialogHeader>

      <DialogContent>
        <VStack
          borderWidth={1}
          borderStyle="solid"
          borderColor={colors.grey300}
          borderRadius={8}
          overflow="hidden"
          p={3}
          gap={2}
        >
          <HStack gap={2} alignItems="flex-start">
            <Box flexShrink={0}>
              <Image
                src={thumbnail}
                alt={title}
                width={64}
                height={64}
                objectFit="cover"
                borderRadius={4}
                loading="lazy"
                loadingFallback={<Skeleton width={64} height={64} />}
              />
            </Box>
            <VStack flex={1} gap={2}>
              <Text color={colors.grey900}>{title}</Text>
              {firstLevelButtonKeys.length > 1 && (
                <HStack gap={3} flexWrap="wrap">
                  {firstLevelButtonKeys.map(firstLevelButton => (
                    <Chip
                      key={firstLevelButton}
                      p={1}
                      variant="square"
                      size="medium"
                      color={firstLevelButton === currentGroup ? 'red' : 'default'}
                      isOutlined
                      isSoft={firstLevelButton === currentGroup}
                      onClick={() => setCurrentGroup(firstLevelButton)}
                    >
                      {firstLevelButton}
                    </Chip>
                  ))}
                </HStack>
              )}
            </VStack>
          </HStack>

          {couponConditions.length > 0 && (
            <VStack gap={1} p={1} borderRadius={4}>
              {couponConditions.map(criterion => (
                <CouponCondition
                  key={criterion.display_name}
                  isApplied={criterion.applied_status === AppliedStatus.APPLIED || criterion.applied_status === AppliedStatus.UNABLE}
                  displayName={criterion.display_name}
                  currency={currency}
                  moreAmount={criterion.left_amount}
                  moreQuantity={criterion.left_quantity ?? criterion.display_criterion_quantity}
                  count={criterion.count}
                />
              ))}
            </VStack>
          )}

          <VStack backgroundColor={colors.grey50} divider={<Divider color={colors.grey200} />}>
            {currentVariations.map(variation => (
              <ProductVariationRow
                key={variation.sku}
                variation={variation}
                currentQuantity={cartItems.find(item => item.cartItemId === variation.variation_id)?.quantity ?? 0}
                onQuantityChange={handleQuantityChange(variation)}
              />
            ))}
          </VStack>
        </VStack>
      </DialogContent>
      <DialogActions justifyContent="space-between">
        <HStack gap={1} alignItems="center">
          <Text variant="caption" color={colors.grey600}>訂購總額</Text>
          <Amount amount={cartTotalPrice} color={colors.red600} isBold />
        </HStack>
        <Button color="red" startIcon={<CartFilled />} onClick={handleNavigateToCart}>
          {`(${cartTotalQuantity.toLocaleString()}) 前往購物車`}
        </Button>
      </DialogActions>
      <DialogDeliveryClass />
      <DialogWeightAttribute />
    </>
  )
}

export const DialogProductCart = ({ dialogKey }: { dialogKey: string }) => {
  const { open, closeDialog, title, thumbnail, variations, basePath } = useDialog<DialogProductCartProps>(dialogKey)
  const isMobile = useResponsive('mobile')

  return (
    <ResponsiveDialog open={open} onClose={closeDialog} maxWidth={480} height={isMobile ? '80vh' : 650} fullWidth disablePortal>
      {open && (
        <DialogProductCartContent
          title={title}
          thumbnail={thumbnail}
          variations={variations}
          basePath={basePath}
          onClose={closeDialog}
        />
      )}
    </ResponsiveDialog>
  )
}
